import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { FaExpandArrowsAlt, FaCompressArrowsAlt } from 'react-icons/fa';
import 'leaflet/dist/leaflet.css';
import MapErrorBoundary from './MapErrorBoundary';
import { getApiUrlAbsolute } from '../config/api';
import './PropertyLocationMap.css';

// Fix for default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Polished basemap (cleaner than default OSM; free, no API key)
const MAP_TILE = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
};

const locationPinIcon = L.divIcon({
  className: 'plc-div-icon-root',
  html: `
    <div class="plc-pin" aria-hidden="true">
      <div class="plc-pin-bubble"></div>
      <div class="plc-pin-stem"></div>
      <div class="plc-pin-pulse"></div>
    </div>
  `,
  iconSize: [44, 56],
  iconAnchor: [22, 54],
  popupAnchor: [0, -50],
});

/**
 * Parse a single coordinate value from backend (string or number).
 * Returns a finite number or null if missing/invalid.
 */
function parseCoord(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (typeof num !== 'number' || !Number.isFinite(num)) return null;
  return num;
}

/**
 * Returns { lat, lng } only when both are valid numbers (not 0,0).
 * Tries: location.lat/lng, latitude/longitude, coordinates, lat/lng, lat/lon.
 */
function getValidCoordinates(property) {
  if (!property || typeof property !== 'object') return null;

  const pairs = [
    [property.location?.lat, property.location?.lng],
    [property.location?.lat, property.location?.lon],
    [property.latitude, property.longitude],
    [property.coordinates?.lat, property.coordinates?.lng],
    [property.lat, property.lng],
    [property.lat, property.lon],
  ];

  for (const [a, b] of pairs) {
    const lat = parseCoord(a);
    const lng = parseCoord(b);
    if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
      // Basic validity: lat in [-90,90], lng in [-180,180]
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
  }
  return null;
}

/** Safe string for display/query (handles object or string) */
function safeStr(value, fallback = '') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return fallback;
}

/** Build address string for geocoding; also returns city-only for fallback try */
function getAddressQueries(property) {
  if (!property || typeof property !== 'object') return { full: '', cityOnly: '' };
  const address = safeStr(property.address);
  const city = safeStr(property.city);
  const state = safeStr(property.state);
  const pincode = property.pincode != null ? String(property.pincode).trim() : '';
  const parts = [address, city, state, pincode].filter(Boolean);
  const full = parts.length > 0 ? parts.join(', ') : city || '';
  const cityOnly = city ? (state ? `${city}, ${state}` : city) : '';
  return { full: full || '', cityOnly };
}

const PropertyLocationMap = ({ 
  property, 
  height = 300,
  showPopup = true,
  showExpandButton = true
}) => {
  const [mapKey, setMapKey] = useState(0);
  const [mapError, setMapError] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [geocodedCoords, setGeocodedCoords] = useState(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Valid coordinates from property (stored lat/lng)
  const coordinates = useMemo(
    () => getValidCoordinates(property),
    [property]
  );

  // Address strings: full (for first try) and city-only (fallback)
  const { full: addressQueryFull, cityOnly: addressQueryCity } = useMemo(
    () => getAddressQueries(property),
    [property]
  );
  const hasAddressToGeocode = Boolean(addressQueryFull || addressQueryCity);

  // When no stored coordinates but we have address, geocode (full then city fallback)
  useEffect(() => {
    if (!isClient || coordinates || geocodedCoords || !hasAddressToGeocode) return;
    let cancelled = false;
    setGeocoding(true);
    setGeocodeFailed(false);

    const tryGeocode = async (query) => {
      if (!query || cancelled) return null;
      const res = await fetch(
        getApiUrlAbsolute(`/api/geocode/search?q=${encodeURIComponent(query)}`)
      );
      const data = await res.json();
      if (cancelled) return null;
      if (data.success && data.lat != null && data.lng != null) {
        const lat = Number(data.lat);
        const lng = Number(data.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      }
      return null;
    };

    (async () => {
      try {
        let coords = await tryGeocode(addressQueryFull);
        if (!coords && addressQueryCity && addressQueryCity !== addressQueryFull) {
          coords = await tryGeocode(addressQueryCity);
        }
        if (!cancelled) {
          if (coords) {
            setGeocodedCoords(coords);
            setGeocodeFailed(false);
          } else {
            setGeocodeFailed(true);
          }
        }
      } catch (err) {
        if (!cancelled) setGeocodeFailed(true);
      } finally {
        if (!cancelled) setGeocoding(false);
      }
    })();
    return () => { cancelled = true; setGeocoding(false); };
  }, [isClient, addressQueryFull, addressQueryCity, hasAddressToGeocode, coordinates, geocodedCoords]);

  // Reset geocoded state when property changes
  useEffect(() => {
    setGeocodedCoords(null);
    setGeocodeFailed(false);
    setGeocoding(false);
  }, [property?.id, property?._id]);

  // Effective coords: stored first, then geocoded from address
  const effectiveCoords = coordinates || geocodedCoords;

  // Ensure we're on client-side only
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Force map re-render when property or effective coords change
  useEffect(() => {
    if (isClient && property && effectiveCoords && effectiveCoords.lat != null && effectiveCoords.lng != null) {
      setMapKey(prev => prev + 1);
      setMapError(false);
      const timer = setTimeout(() => setIsMapReady(true), 100);
      return () => clearTimeout(timer);
    }
    setIsMapReady(false);
  }, [isClient, property?.id, property?._id, effectiveCoords?.lat, effectiveCoords?.lng]);

  // Close fullscreen on Escape
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen map is open
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isFullscreen]);

  // Helper to safely get string value
  const getSafeString = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.name) return String(value.name);
      if (value.value) return String(value.value);
      if (value.city) return String(value.city);
      return fallback;
    }
    return String(value);
  };

  // No property or map error: show fallback
  if (mapError || !property) {
    return (
      <div className="map-fallback">
        <div className="map-fallback-icon">📍</div>
        <p className="map-fallback-text">
          Map location not available for this property
        </p>
        <p className="map-fallback-address">
          {getSafeString(property?.address) || `${getSafeString(property?.city, 'Unknown Location')}${getSafeString(property?.state) ? `, ${getSafeString(property.state)}` : ', India'}`}
        </p>
      </div>
    );
  }

  // No coordinates, and no address to geocode or geocode failed (and not still loading): show fallback
  if (!effectiveCoords && !geocoding && (!hasAddressToGeocode || geocodeFailed)) {
    return (
      <div className="map-fallback">
        <div className="map-fallback-icon">📍</div>
        <p className="map-fallback-text">
          Map location not available for this property
        </p>
        <p className="map-fallback-address">
          {getSafeString(property?.address) || `${getSafeString(property?.city, 'Unknown Location')}${getSafeString(property?.state) ? `, ${getSafeString(property.state)}` : ', India'}`}
        </p>
      </div>
    );
  }

  // Waiting for geocode or map not ready: show loading
  if (!effectiveCoords || !isClient || !isMapReady) {
    return (
      <div className="map-fallback">
        <div className="map-fallback-icon">📍</div>
        <p className="map-fallback-text">Loading map...</p>
      </div>
    );
  }

  const fullscreenOverlay = (
    <div className="map-fullscreen-overlay" role="dialog" aria-label="Map fullscreen">
      <div className="map-fullscreen-header">
        <span className="map-fullscreen-title">
          {getSafeString(property?.name, 'Property')} – Location
        </span>
        <button
          type="button"
          className="map-fullscreen-close"
          onClick={() => setIsFullscreen(false)}
          aria-label="Close fullscreen map"
        >
          <FaCompressArrowsAlt aria-hidden />
          <span>Close</span>
        </button>
      </div>
      <div className="map-fullscreen-body">
        <MapErrorBoundary property={property}>
          <MapContainer
            key={`${mapKey}-fs`}
            center={[effectiveCoords.lat, effectiveCoords.lng]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
            zoomControl={true}
          >
            <TileLayer attribution={MAP_TILE.attribution} url={MAP_TILE.url} />
            <Marker position={[effectiveCoords.lat, effectiveCoords.lng]} icon={locationPinIcon}>
              {showPopup && property && (
                <Popup className="plc-popup" closeButton={true}>
                  <div className="map-popup-content">
                    <strong>{getSafeString(property.name, 'Property')}</strong>
                    {getSafeString(property.address) && <p>{getSafeString(property.address)}</p>}
                    {getSafeString(property.city) && <p>{getSafeString(property.city)}</p>}
                  </div>
                </Popup>
              )}
            </Marker>
          </MapContainer>
        </MapErrorBoundary>
      </div>
    </div>
  );

  return (
    <MapErrorBoundary property={property}>
      <div className="property-location-map-wrapper">
        <div className="property-location-map">
          <MapContainer
            key={`${mapKey}-inline`}
            center={[effectiveCoords.lat, effectiveCoords.lng]}
            zoom={15}
            className="plc-map-container"
            style={{ height: `${height}px`, width: '100%' }}
            scrollWheelZoom={false}
            zoomControl={true}
          >
            <TileLayer attribution={MAP_TILE.attribution} url={MAP_TILE.url} />
            <Marker position={[effectiveCoords.lat, effectiveCoords.lng]} icon={locationPinIcon}>
              {showPopup && property && (
                <Popup className="plc-popup" closeButton={true}>
                  <div className="map-popup-content">
                    <strong>{getSafeString(property.name, 'Property')}</strong>
                    {getSafeString(property.address) && <p>{getSafeString(property.address)}</p>}
                    {getSafeString(property.city) && <p>{getSafeString(property.city)}</p>}
                  </div>
                </Popup>
              )}
            </Marker>
          </MapContainer>
          {showExpandButton && (
            <button
              type="button"
              className="map-expand-btn"
              onClick={() => setIsFullscreen(true)}
              aria-label="Expand map to fullscreen"
              title="Expand map"
            >
              <FaExpandArrowsAlt />
            </button>
          )}
        </div>
      </div>
      {isFullscreen && createPortal(fullscreenOverlay, document.body)}
    </MapErrorBoundary>
  );
};

export default PropertyLocationMap;

